import axios from 'axios'
import ParamsUtil from 'utils/params-util'

const API_PATH = 'api/v2/admins/finance/deals'
const BUYING_API_PATH = 'api/v2/admins/buying/deals'

const SalesDealsApi = {

  /**
   * Get all OR filtered Deals
   * Invoke `GET admins/outbound/deals
   * @param {String} q - query to search by deal or payment reference numbers
   */
  get: async (rawParams = {}) => {
    const defaults = {
      page     : 1,
      per_page : 10,
      sort_by  : 'deals.created_at',
      sort_dir : 'desc',
      scope    : 'need_action', // align with frontend default
    }

    // Merge and drop empty keys so only real filters go to the server
    const params = ParamsUtil.removeEmptyAttributes({ ...defaults, ...rawParams })

    return axios.get(API_PATH, { params })
  },

  /**
   * Get specific deals
   * Invoke `GET admins/sales/deals/:id
   * @param {Number} dealId - id of Deal
   */
  show: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get history list of a deal
   * Invoke `GET admins/sales/deals/:id/list_history
   * @param {Number} dealId - id of Deal
   */
  listHistory: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/list_history`

    const response = await axios.get(path)

    return response
  },

  /**
   * Get specific history of a deal
   * Invoke `GET admins/sales/deals/get_history
   * @param {Number} historyId - id of Deal
   */
  getHistory: async ({ historyId }) => {
    const path = `${API_PATH}/get_history?history_id=${historyId}`

    const response = await axios.get(path)

    return response
  },


  /**
   * Send invoice PDF via email
   * Invoke POST admins/sales/deals/:id/send_invoice_email
   */
  sendInvoiceEmail: async ({ dealId, formData }) => {
    const path = `${API_PATH}/${dealId}/send_invoice_email`

    // IMPORTANT: axios will keep baseURL + auth automatically
    const response = await axios.post(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response
  },

  /**
   * Create new payment (termin) for an existing deal
   * Invoke `POST admins/sales/deals/:id/payments`
   *
   * @param {Number} dealId
   * @param {Object} payload
   * @param {String} payload.method_type
   * @param {Number} payload.amount
   * @param {Number} payload.installment
   */
  createPayment: async ({ dealId, payload }) => {
    const path = `${API_PATH}/${dealId}/payments`

    const response = await axios.post(path, payload)

    return response
  },

  /**
   * Upload transfer receipt for a deal payment
   * Invoke `PUT admins/sales/deals/:id/upload_transfer_receipt`
   *
   * @param {Number} dealId
   * @param {Number} paymentId (optional, future-proof)
   * @param {FormData} formData
   */
  uploadTransferReceipt: async ({ dealId, paymentId, formData }) => {
    const path = `${API_PATH}/${dealId}/upload_transfer_receipt`

    // Keep backward compatible, future-proof
    if (paymentId) {
      formData.append('payment_id', String(paymentId))
    }

    const response = await axios.put(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response
  },

  /**
   * Get metadata for creating BKO product (designer, category, condition, group)
   * Invoke `GET admins/sales/deals/bko_product_meta`
   */
  poProductMeta: async () => {
    const path = `${API_PATH}/po_product_meta`

    const response = await axios.get(path)

    return response
  },

  /**
   * Create draft BKO product (free text product for pre-order deal)
   * Invoke `POST admins/sales/deals/create_bko_product`
   *
   * @param {Object} product
   * @param {String} product.supplier_name
   * @param {Number} product.designer_id
   * @param {Number} product.category_id
   * @param {Number} product.condition_id
   * @param {String} product.name
   * @param {String} product.p_size
   * @param {Number} product.default_price
   * @param {Number} product.selling_price
   */
  createBkoProductDraft: async ({ product }) => {
    const path = `${API_PATH}/create_bko_product`

    const params = {
      product,
    }

    const response = await axios.post(path, params)

    return response
  },

  uploadDownPaymentProof: async (dealId, files = []) => {
    const path = `${API_PATH}/${dealId}/upload_down_payment_proof`

    const formData = new FormData()
    files.forEach((file) => {
      formData.append('proofs[]', file)
    })

    const response = await axios.post(path, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return response
  },

  /**
   * Cancel specific deals
   * Invoke `GET admins/sales/deals/:id/cancel
   * @param {Number} dealId - id of Deal
   */
  cancel: async ({ dealId }) => {
    const path = `${API_PATH}/${dealId}/cancel`

    const response = await axios.post(path)

    return response
  },


  buyingPoSourcingQueue: async (params = {}) => {
    const path = `${BUYING_API_PATH}`

    const response = await axios.get(path, {
      params,
    })

    return response
  },

  buyingPoSourcingDetail: async (dealId) => {
    const path = `${BUYING_API_PATH}/${dealId}`

    const response = await axios.get(path)

    return response
  },

  buyingStartSourcing: async (dealId, payload = {}) => {
    const path = `${BUYING_API_PATH}/${dealId}/start_sourcing`

    const response = await axios.post(path, payload)

    return response
  },

  buyingSourced: async (dealId, payload = {}) => {
    const path = `${BUYING_API_PATH}/${dealId}/sourced`

    const response = await axios.post(path, payload)

    return response
  },

  buyingProductMeta: async () => {
    const path = `${BUYING_API_PATH}/product_meta`
    return axios.get(path)
  },

  salesProductMeta: async () => {
    const path = `${API_PATH}/product_meta`
    return axios.get(path)
  },


}

export default SalesDealsApi
